import CameraAltIcon from '@mui/icons-material/CameraAlt' // Import MUI icon
import CloseIcon from '@mui/icons-material/Close'
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline' // Import MUI icon
import FolderOpenIcon from '@mui/icons-material/FolderOpen' // Import MUI icon
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf' // Import MUI icon
import {
  Alert,
  Avatar,
  Box,
  Button,
  ButtonProps,
  Grid,
  IconButton,
  LinearProgress,
  List,
  ListItem,
  ListItemAvatar,
  ListItemButton,
  ListItemText,
  Snackbar,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material'
import { grey, red } from '@mui/material/colors' // Import MUI colors
import * as Sentry from '@sentry/react'
import {
  ErrorMessage,
  FormikFormProps,
  FormikValues,
  useField,
  useFormikContext,
} from 'formik' // Import formik hook
import { FC, InputHTMLAttributes, useRef, useState } from 'react' // Import MUI components

import dayjs from 'dayjs' // Import date library

import { PDFDocument } from 'pdf-lib' // Import pdf-lib
import { useTranslation } from 'react-i18next'

import { MultipleDocumentsType, SnackBarAlertType } from '../../types/interface' // Import interface definition

import { checkFileExists, uint8ArrayToS3 } from '../../../hooks/useS3Storage'
import {
  DocumentType,
  getFileRestriction,
} from '../../utils/documentRestrictions'
import { mergePdf, newPdf } from '../../utils/pdf-document' // Import pdf utils

interface MultipleUploadWrapperProps
  extends InputHTMLAttributes<HTMLInputElement> {
  city?: string
  disabled?: boolean
  documentType?: DocumentType
  folder: string
  label: string
  name: string
  value?: string
}

const MultipleUploadWrapper: FC<MultipleUploadWrapperProps> = ({
  city,
  disabled,
  documentType = 'document',
  folder,
  label,
  name,
}) => {
  const [, meta] = useField(name)
  const inputRef = useRef<HTMLInputElement>(null)
  const [files, setFiles] = useState<MultipleDocumentsType[]>([])
  const [loading, setLoading] = useState<boolean>(false)
  const [limited, setLimited] = useState<boolean>(false)
  const theme = useTheme()
  const isLargeScreen = useMediaQuery(theme.breakpoints.up('md'))
  const { setFieldError, setFieldTouched, setFieldValue, setSubmitting } =
    useFormikContext<FormikFormProps & FormikValues>()
  const [alertEvent, setAlertEvent] = useState<SnackBarAlertType>({
    message: '',
    open: false,
    type: 'info',
  })
  const { t } = useTranslation()

  const fileRestriction = getFileRestriction({ city, documentType })

  const configGridItem = {
    item: true,
    sx: {
      alignItems: 'center',
      display: 'flex',
      flexDirection: 'column',
      my: 1,
    },
    xs: 12,
  }

  const isValid = (file: File): boolean => {
    const MAX_FILE_SIZE = 5242880 // 5MB

    // Check file size first
    if (file.size > MAX_FILE_SIZE) {
      setFieldError(name, 'File size exceeds 5MB limit')
      return false
    }

    // Check file type against restrictions
    if (fileRestriction.formats.includes(file.type)) {
      return true
    }

    // Use restriction-specific error message
    setFieldError(name, fileRestriction.errorMessage)
    return false
  }

  const uploadToS3 = async (mergedPdf: Uint8Array, isLimited = false) => {
    try {
      Sentry.addBreadcrumb({
        category: 'upload',
        level: 'info',
        message: `Uploading multiple files - folder: [${folder}] - label: [${label}]`,
      })

      setSubmitting(true)
      const response = await uint8ArrayToS3(
        mergedPdf,
        `${folder}`,
        `${label}_${dayjs().format('YYYY-MM-DD')}`
      )

      await checkFileExists(response)

      setFieldValue(name, response)
      setSubmitting(false)
      setFieldTouched(name, true)
      setLoading(false)
      setLimited(isLimited)
    } catch (error) {
      Sentry.captureException(error)

      setAlertEvent({
        message: t('documents.alerts.error'),
        open: true,
        type: 'error',
      })

      setSubmitting(false)
      setLoading(false)
      setLimited(false)
    }
  }

  const addFile = async (file: File) => {
    setLoading(true)
    const reader = new FileReader()
    if (file.type !== 'application/pdf') {
      reader.readAsDataURL(file) // Read image as data url
      reader.onloadend = async () => {
        // When reader is done
        const newpdf = await newPdf(file) // Create pdf from image
        setFiles([
          ...files,
          { file: reader.result!, name: file.name, pdf: newpdf },
        ]) // Add file to array
        const mergedPdf = await mergePdf([
          ...files,
          { file: reader.result!, name: file.name, pdf: newpdf },
        ]) // Merge pdfs
        await uploadToS3(mergedPdf) // Upload to s3
      }
    } else {
      reader.readAsArrayBuffer(file) // Read pdf as array buffer
      reader.onloadend = async () => {
        // When reader is done
        const byteArray = new Uint8Array(reader.result as ArrayBuffer) // Convert array buffer to uint8array
        setFiles([
          ...files,
          { file: URL.createObjectURL(file), name: file.name, pdf: byteArray },
        ]) // Add file to array
        const isProtected = await PDFDocument.load(byteArray, {
          ignoreEncryption: true,
        }).then((pdfDoc) => pdfDoc.isEncrypted) // Check if pdf is protected
        const mergedPdf = isProtected
          ? byteArray
          : await mergePdf([
              ...files,
              {
                file: URL.createObjectURL(file),
                name: file.name,
                pdf: byteArray,
              },
            ]) // Merge pdfs
        await uploadToS3(mergedPdf, isProtected) // Upload to s3
      }
    }
  }

  const configInput: InputHTMLAttributes<HTMLInputElement> = {
    accept: fileRestriction.extensions.join(', '),
    hidden: true,
    onChange: (e) => {
      if (isValid(e.target.files![0])) {
        addFile(e.target.files![0])
      } else {
        setFieldTouched(name, true, false)
      }
    },
    type: 'file',
  }

  const buttonConfig: ButtonProps = {
    color: meta && meta.touched && meta.error ? 'error' : 'primary',
    disabled: disabled || loading || limited,
    fullWidth: true,
    onClick: () => {
      inputRef.current?.click()
    },
    sx: { py: 4 },
    variant: 'outlined',
  }

  const boxConfig = {
    sx: {
      alignItems: 'center',
      display: 'flex',
      flexDirection: 'column',
    },
  }

  // Find file from array and remove it
  const removeFile = async (file: MultipleDocumentsType) => {
    const index = files.indexOf(file)
    if (index > -1) {
      files.splice(index, 1)
    }
    setFiles([...files])
    const mergedPdf = await mergePdf(files)
    await uploadToS3(mergedPdf)
    if (files.length === 0) {
      setFieldValue(name, '')
    }
    // downloadPdf(mergedPdf, 'alejandro_deleted.pdf', 'application/pdf')
  }

  const handleClose = () => {
    setAlertEvent((prevAlertEvent) => ({ ...prevAlertEvent, open: false }))
  }

  return (
    <>
      <Grid container>
        <Grid {...configGridItem}>
          <Typography color="text.primary" variant="subtitle1">
            {label}
          </Typography>
          <Typography color="text.secondary" variant="caption">
            {t('documents.multiple_upload.description')}
          </Typography>
        </Grid>
        <Grid {...configGridItem}>
          <input value="" {...configInput} ref={inputRef} />
          <Button {...buttonConfig}>
            <Box {...boxConfig}>
              {isLargeScreen ? (
                <FolderOpenIcon fontSize="large" />
              ) : (
                <CameraAltIcon fontSize="large" />
              )}
              {t('documents.multiple_upload.add_pictures')}
              <Typography sx={{ textTransform: 'lowercase' }}>
                {t(
                  'documents.multiple_upload.or_upload_files_from_your_gallery'
                )}
              </Typography>
              <Typography variant="caption">
                {t('documents.multiple_upload.max_size')}
              </Typography>
            </Box>
          </Button>
        </Grid>
        <Grid {...configGridItem}>
          <ErrorMessage name={name}>
            {(msg) => (
              <Typography sx={{ color: red[600] }} variant="caption">
                {msg}
              </Typography>
            )}
          </ErrorMessage>
        </Grid>
        <Grid {...configGridItem}>
          <List sx={{ maxWidth: isLargeScreen ? 443 : 360, width: '100%' }}>
            {files.map((file, index) => (
              <ListItem
                disablePadding
                divider
                key={index}
                secondaryAction={
                  <IconButton
                    aria-label="delete"
                    edge="end"
                    onClick={() => removeFile(file)}
                  >
                    <DeleteOutlineIcon />
                  </IconButton>
                }
              >
                <ListItemButton dense>
                  <ListItemAvatar sx={{ mr: 2 }}>
                    <Avatar
                      src={`${file.file}`}
                      sx={{ bgcolor: grey[100], height: 56, width: 56 }}
                      variant="square"
                    >
                      <PictureAsPdfIcon sx={{ color: grey[600] }} />
                    </Avatar>
                  </ListItemAvatar>
                  <ListItemText
                    primary={file.name}
                    primaryTypographyProps={{ noWrap: true }}
                    secondary={loading && <LinearProgress />}
                  />
                </ListItemButton>
              </ListItem>
            ))}
          </List>
        </Grid>
      </Grid>
      <Snackbar
        anchorOrigin={{ horizontal: 'center', vertical: 'bottom' }}
        autoHideDuration={6000}
        onClose={handleClose}
        open={alertEvent.open}
      >
        <Alert
          action={
            <IconButton
              aria-label="close"
              color="inherit"
              onClick={handleClose}
              size="small"
            >
              <CloseIcon />
            </IconButton>
          }
          elevation={2}
          onClose={handleClose}
          severity={alertEvent.type}
          sx={{
            width: '100%',
          }}
        >
          {alertEvent.message}
        </Alert>
      </Snackbar>
    </>
  )
}

export default MultipleUploadWrapper
